/**
 * Detalhe da partida.
 *
 * Concentra presença, lista de espera, convidados, times e placar. No desktop
 * a lista de presença e os times ficam lado a lado — é a vantagem real de ter
 * uma tela grande para o que no celular exige rolar.
 */

import type { GamePlayer, PlayerPosition } from '@nossorachao/types';
import { relativeDayLabel } from '@nossorachao/utils';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Hand,
  MapPin,
  Navigation,
  Shuffle,
  Star,
  UserMinus,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import { ShareTeamsButtons } from '@/components/share-teams';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PositionBadge,
  PositionPicker,
  ProgressBar,
  SectionTitle,
  Skeleton,
  StarRating,
  Tabs,
  capitalizeFirst,
  cx,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { teamsToWhatsappText } from '@/lib/teams-text';
import {
  useAddGuest,
  useFinishGame,
  useGame,
  useGroup,
  useRemoveGuest,
  useRemovePlayer,
  useRespondAttendance,
} from '@/lib/queries';
import { useAuth } from '@/store/auth';
import { Modal } from './groups';

type RosterTab = 'confirmed' | 'waitlist' | 'declined' | 'pending';

type RemovalTarget = { kind: 'player'; memberId: string; name: string } | { kind: 'guest'; guestId: string; name: string };

export function GameDetailPage() {
  const { gameId = '' } = useParams();
  const navigate = useNavigate();

  const { data: game, isLoading } = useGame(gameId);
  const respond = useRespondAttendance(gameId);
  const finish = useFinishGame(gameId);

  // Sortear times e encerrar a partida são ações de administrador: a API já
  // recusa, mas oferecer o botão para depois dar 403 é enganar o usuário.
  const group = useGroup(game?.groupId ?? '');
  const isAdmin = group.data ? group.data.myRole !== 'MEMBER' : false;

  const [tab, setTab] = useState<RosterTab>('confirmed');
  const [guestModal, setGuestModal] = useState(false);
  const [removal, setRemoval] = useState<RemovalTarget | null>(null);
  const myUserId = useAuth((state) => state.user?.id);

  if (isLoading || !game) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const scheduledAt = new Date(game.scheduledAt);
  const finished = game.status === 'FINISHED';
  const canceled = game.status === 'CANCELED';
  const canRespond = !finished && !canceled;

  const roster: Record<RosterTab, GamePlayer[]> = {
    confirmed: game.roster.confirmed,
    waitlist: game.roster.waitlist,
    declined: game.roster.declined,
    pending: game.roster.noResponse,
  };

  // Quem confirmou ou foi convidado depois do sorteio não está em time nenhum.
  const inTeams = new Set(
    game.teams.flatMap((team) =>
      team.players.map((player) => player.member?.id ?? player.guest?.id),
    ),
  );
  const outOfTeams =
    game.teams.length === 0
      ? 0
      : game.roster.confirmed.filter((player) => !inTeams.has(player.member.id)).length +
        game.roster.guests.filter((guest) => !inTeams.has(guest.id)).length;

  const when = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(scheduledAt);

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(scheduledAt);

  return (
    <>
      <PageHeader
        title={game.groupName}
        subtitle={`${capitalizeFirst(when)} · ${time}`}
        back={{ to: `/grupos/${game.groupId}`, label: 'Grupo' }}
        action={
          canRespond ? (
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Button
                variant={game.myAttendance === 'CONFIRMED' ? 'success' : 'secondary'}
                icon={CheckCircle2}
                loading={respond.isPending && respond.variables === 'CONFIRMED'}
                onClick={() => respond.mutate('CONFIRMED')}
              >
                Estou dentro
              </Button>
              <Button
                variant={game.myAttendance === 'DECLINED' ? 'danger' : 'secondary'}
                icon={XCircle}
                loading={respond.isPending && respond.variables === 'DECLINED'}
                onClick={() => respond.mutate('DECLINED')}
              >
                Estou fora
              </Button>
            </div>
          ) : null
        }
      />

      {respond.data?.wentToWaitlist ? (
        <Card accent="rgb(21 112 239)" className="mb-4">
          <p className="text-sm">
            Pelada lotada — você entrou na <strong>lista de espera</strong> na posição{' '}
            {respond.data.waitlistPosition}. Avisamos assim que abrir vaga.
          </p>
        </Card>
      ) : null}

      {respond.error ? (
        <Card accent="rgb(217 45 32)" className="mb-4">
          <p className="text-sm text-bad">
            {respond.error instanceof ApiError
              ? respond.error.message
              : 'Não foi possível registrar sua resposta.'}
          </p>
        </Card>
      ) : null}

      {/* min-w-0: sem ele a coluna cresce até caber o conteúdo e a página
          rola de lado no celular. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Coluna principal */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card accent={canceled ? 'rgb(217 45 32)' : 'rgb(18 183 106)'}>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="brand">{relativeDayLabel(scheduledAt)}</Badge>
                {canceled ? <Badge tone="bad">Cancelada</Badge> : null}
                {finished ? <Badge tone="neutral">Encerrada</Badge> : null}
              </div>

              <div className="space-y-1.5 text-sm text-ink-2">
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-ink-3" aria-hidden />
                  {time}
                  {game.endsAt
                    ? ` – ${new Intl.DateTimeFormat('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }).format(new Date(game.endsAt))}`
                    : ''}
                </p>

                {game.venueName ? (
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-ink-3" aria-hidden />
                    {game.venueName}
                    {game.venueMapsUrl ? (
                      <a
                        href={game.venueMapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-1 inline-flex items-center gap-1 text-brand hover:underline"
                      >
                        <Navigation className="h-3.5 w-3.5" aria-hidden />
                        Como chegar
                      </a>
                    ) : null}
                  </p>
                ) : null}
              </div>

              {game.notes ? (
                <p className="rounded-lg bg-sunken p-3 text-[13px] text-ink-2">{game.notes}</p>
              ) : null}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="tabular font-semibold">
                    {game.confirmedCount} / {game.maxPlayers} confirmados
                  </span>
                  {game.roster.guests.length > 0 ? (
                    <span className="text-ink-3">
                      inclui {game.roster.guests.length} convidado(s)
                    </span>
                  ) : null}
                </div>
                <ProgressBar value={game.confirmedCount} max={game.maxPlayers} />
              </div>
            </div>
          </Card>

          {/* Avaliação */}
          {finished && game.hasPendingRatings ? (
            <Card accent="rgb(220 104 3)">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-warn-soft">
                    <Star className="h-4 w-4 text-warn" aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold">Avalie os jogadores</p>
                    <p className="text-[13px] text-ink-2">
                      Anônimo. Ninguém descobre quem deu qual nota.
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate(`/partidas/${gameId}/avaliar`)}>Avaliar</Button>
              </div>
            </Card>
          ) : null}

          {/* Times */}
          {game.teams.length > 0 ? (
            <section className="space-y-3">
              <SectionTitle
                action={
                  isAdmin ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={Shuffle}
                      onClick={() => navigate(`/partidas/${gameId}/times`)}
                    >
                      Ajustar
                    </Button>
                  ) : null
                }
              >
                Times
              </SectionTitle>

              {isAdmin && outOfTeams > 0 && canRespond ? (
                <Card accent="rgb(220 104 3)">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="flex items-start gap-2 text-[13px] text-ink-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
                      {outOfTeams} confirmado(s) ou convidado(s) fora dos times — entraram depois
                      do sorteio.
                    </p>
                    <Button
                      size="sm"
                      icon={Shuffle}
                      onClick={() => navigate(`/partidas/${gameId}/times`)}
                    >
                      Colocar nos times
                    </Button>
                  </div>
                </Card>
              ) : null}

              <ShareTeamsButtons
                text={teamsToWhatsappText({
                  groupName: game.groupName,
                  scheduledAt: game.scheduledAt,
                  venueName: game.venueName,
                  teams: game.teams.map((team) => ({
                    name: team.name,
                    color: team.color,
                    players: team.players.map((player) => ({
                      name: player.name,
                      isGoalkeeper: player.isGoalkeeper,
                      isGuest: Boolean(player.guest),
                    })),
                  })),
                })}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                {game.teams.map((team) => (
                  <Card key={team.id} accent={team.color}>
                    <div className="mb-3 flex items-center justify-between">
                      <span className="flex items-center gap-2 font-bold">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: team.color }}
                        />
                        {team.name}
                      </span>
                      <span className="flex items-center gap-2">
                        {!team.hasGoalkeeper ? <Badge tone="warn">Sem goleiro</Badge> : null}
                        {team.averageSkill !== null ? (
                          <span className="tabular text-[13px] font-semibold text-ink-2">
                            {team.averageSkill.toFixed(2).replace('.', ',')}
                          </span>
                        ) : null}
                      </span>
                    </div>

                    <ul className="space-y-2">
                      {[...team.players]
                        .sort((a, b) => Number(b.isGoalkeeper) - Number(a.isGoalkeeper))
                        .map((player) => (
                        <li key={player.id} className="flex items-center gap-2.5">
                          <Avatar
                            name={player.name}
                            size={28}
                            badge={player.isGoalkeeper ? Hand : undefined}
                          />
                          <span className="min-w-0 flex-1 truncate text-[13px]">
                            {player.name}
                            {player.guest ? (
                              <span className="ml-1.5 text-[11px] text-ink-3">convidado</span>
                            ) : null}
                          </span>
                          <PositionBadge position={player.position} short />
                          {player.skillRating !== null ? (
                            <Skill value={player.skillRating} />
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </section>
          ) : null}

          {/* Placar */}
          {game.matches.length > 0 ? (
            <section className="space-y-3">
              <SectionTitle>Placar</SectionTitle>
              {game.matches.map((match) => {
                const home = game.teams.find((team) => team.id === match.homeTeamId);
                const away = game.teams.find((team) => team.id === match.awayTeamId);

                return (
                  <Card key={match.id}>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex-1 truncate font-semibold">
                        {home?.name ?? 'Time A'}
                      </span>
                      <span className="tabular flex items-center gap-2 text-2xl font-extrabold">
                        {match.homeScore}
                        <span className="text-ink-3">×</span>
                        {match.awayScore}
                      </span>
                      <span className="flex-1 truncate text-right font-semibold">
                        {away?.name ?? 'Time B'}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </section>
          ) : null}
        </div>

        {/* Coluna lateral: presença */}
        <div className="min-w-0 space-y-4">
          <SectionTitle
            action={
              canRespond ? (
                <Button size="sm" variant="ghost" icon={UserPlus} onClick={() => setGuestModal(true)}>
                  Convidado
                </Button>
              ) : null
            }
          >
            Quem vai
          </SectionTitle>

          <Tabs<RosterTab>
            active={tab}
            onChange={setTab}
            tabs={[
              // Convidado está dentro: aparece na lista "Convidados" logo abaixo.
              {
                key: 'confirmed',
                label: 'Dentro',
                count: roster.confirmed.length + game.roster.guests.length,
              },
              { key: 'waitlist', label: 'Espera', count: roster.waitlist.length },
              { key: 'declined', label: 'Fora', count: roster.declined.length },
              { key: 'pending', label: 'Sem resposta', count: roster.pending.length },
            ]}
          />

          <Card className="p-0">
            {roster[tab].length === 0 ? (
              <p className="p-5 text-center text-[13px] text-ink-3">Ninguém nesta lista.</p>
            ) : (
              <ul className="divide-y divide-line">
                {roster[tab].map((player) => (
                  <li key={player.id} className="flex items-center gap-2.5 p-3">
                    {tab === 'waitlist' && player.waitlistPosition ? (
                      <span className="tabular flex h-6 w-6 items-center justify-center rounded-full bg-info-soft text-[11px] font-bold text-info">
                        {player.waitlistPosition}
                      </span>
                    ) : null}

                    <Avatar
                      name={player.member.user.name}
                      src={player.member.user.avatarUrl}
                      size={32}
                      badge={player.member.position === 'GOALKEEPER' ? Hand : undefined}
                    />

                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {player.member.nickname ?? player.member.user.name}
                    </span>

                    <PositionBadge
                      position={player.member.position}
                      canPlayGoalkeeper={player.member.canPlayGoalkeeper}
                      short
                    />

                    {player.member.skillRating !== null ? (
                      <Skill value={player.member.skillRating} sideColumn />
                    ) : null}

                    {isAdmin && canRespond && (tab === 'confirmed' || tab === 'waitlist') ? (
                      <RemoveButton
                        label={`Remover ${player.member.user.name} da partida`}
                        onClick={() =>
                          setRemoval({
                            kind: 'player',
                            memberId: player.member.id,
                            name: player.member.nickname ?? player.member.user.name,
                          })
                        }
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {game.roster.guests.length > 0 ? (
            <>
              <SectionTitle>Convidados</SectionTitle>
              <Card className="p-0">
                <ul className="divide-y divide-line">
                  {game.roster.guests.map((guest) => (
                    <li key={guest.id} className="flex items-center gap-2.5 p-3">
                      <Avatar name={guest.name} size={32} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px]">{guest.name}</p>
                        <p className="truncate text-[11px] text-ink-3">
                          Convidado por {guest.invitedBy.displayName || guest.invitedBy.name}
                        </p>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <Badge tone="info">Convidado</Badge>
                          <PositionBadge
                            position={guest.position}
                            canPlayGoalkeeper={guest.canPlayGoalkeeper}
                            short
                          />
                        </div>
                      </div>
                      {guest.skillRating !== null ? (
                        <Skill value={guest.skillRating} sideColumn />
                      ) : null}
                      {/* A API aceita o admin ou quem trouxe o convidado. */}
                      {canRespond && (isAdmin || guest.invitedBy.id === myUserId) ? (
                        <RemoveButton
                          label={`Remover o convidado ${guest.name}`}
                          onClick={() =>
                            setRemoval({ kind: 'guest', guestId: guest.id, name: guest.name })
                          }
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </Card>
            </>
          ) : null}

          {isAdmin && !finished && !canceled && game.teams.length === 0 ? (
            <Button
              variant="secondary"
              full
              icon={Shuffle}
              onClick={() => navigate(`/partidas/${gameId}/times`)}
            >
              Sortear times
            </Button>
          ) : null}

          {isAdmin && !finished && !canceled && game.teams.length > 0 ? (
            <Button
              variant="secondary"
              full
              loading={finish.isPending}
              onClick={() => finish.mutate()}
            >
              Encerrar e abrir avaliações
            </Button>
          ) : null}
        </div>
      </div>

      {canceled ? (
        <Card accent="rgb(217 45 32)" className="mt-6">
          <EmptyState
            icon={XCircle}
            title="Partida cancelada"
            description="O administrador cancelou esta pelada."
          />
        </Card>
      ) : null}

      {guestModal ? <GuestModal gameId={gameId} onClose={() => setGuestModal(false)} /> : null}
      {removal ? (
        <RemoveModal gameId={gameId} target={removal} onClose={() => setRemoval(null)} />
      ) : null}
    </>
  );
}

/**
 * Nota do jogador: estrelas quando há espaço, número compacto quando não — as
 * cinco estrelas espremem o nome no celular e na coluna lateral do desktop
 * (`sideColumn`), que no lg+ tem só ~300 px.
 */
function Skill({ value, sideColumn = false }: { value: number; sideColumn?: boolean }) {
  return (
    <>
      <span className={cx('hidden sm:inline-flex', sideColumn && 'lg:hidden')}>
        <StarRating value={value} size={11} showValue={false} />
      </span>
      <span
        className={cx(
          'tabular inline-flex shrink-0 items-center gap-0.5 text-[12px] font-semibold text-ink-2 sm:hidden',
          sideColumn && 'lg:inline-flex',
        )}
      >
        <Star className="h-3 w-3 fill-warn text-warn" aria-hidden />
        {value.toFixed(1).replace('.', ',')}
      </span>
    </>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-bad/10 hover:text-bad"
    >
      <UserMinus size={16} />
    </button>
  );
}

function RemoveModal({
  gameId,
  target,
  onClose,
}: {
  gameId: string;
  target: RemovalTarget;
  onClose: () => void;
}) {
  const removePlayer = useRemovePlayer(gameId);
  const removeGuest = useRemoveGuest(gameId);
  const pending = removePlayer.isPending || removeGuest.isPending;
  const error = removePlayer.error ?? removeGuest.error;

  function handleConfirm() {
    if (target.kind === 'player') {
      removePlayer.mutate(target.memberId, { onSuccess: onClose });
    } else {
      removeGuest.mutate(target.guestId, { onSuccess: onClose });
    }
  }

  return (
    <Modal title="Remover da partida" onClose={onClose}>
      <p className="text-sm text-ink-2">
        {target.kind === 'player' ? (
          <>
            <strong>{target.name}</strong> sai da lista desta partida e vai para "Fora". Se havia
            fila de espera, o primeiro dela assume a vaga.
          </>
        ) : (
          <>
            O convidado <strong>{target.name}</strong> será removido desta partida.
          </>
        )}
      </p>
      {error ? (
        <p className="mt-3 text-[13px] text-bad">
          {error instanceof ApiError ? error.message : 'Não foi possível remover.'}
        </p>
      ) : null}
      <div className="mt-5 flex gap-3">
        <Button variant="secondary" full onClick={onClose}>
          Voltar
        </Button>
        <Button variant="danger" full loading={pending} onClick={handleConfirm}>
          Remover
        </Button>
      </div>
    </Modal>
  );
}

function GuestModal({ gameId, onClose }: { gameId: string; onClose: () => void }) {
  const add = useAddGuest(gameId);
  const [name, setName] = useState('');
  const [skill, setSkill] = useState(3);
  const [position, setPosition] = useState<PlayerPosition>('MIDFIELDER');
  const [canPlayGoalkeeper, setCanPlayGoalkeeper] = useState(false);

  const isGoalkeeper = position === 'GOALKEEPER';

  function changePosition(next: PlayerPosition) {
    setPosition(next);
    if (next === 'GOALKEEPER') setCanPlayGoalkeeper(true);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await add.mutateAsync({
        name: name.trim(),
        skillRating: skill,
        position,
        canPlayGoalkeeper,
      });
      onClose();
    } catch {
      // Erro exibido no campo.
    }
  }

  return (
    <Modal title="Adicionar convidado" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Nome"
          error={add.error instanceof ApiError ? add.error.message : undefined}
        >
          <Input
            placeholder="Ex.: Lucas (amigo do Pedro)"
            value={name}
            onChange={(event) => setName(event.target.value)}
            invalid={Boolean(add.error)}
            autoFocus
          />
        </Field>

        <Field label="Posição">
          <PositionPicker value={position} onChange={changePosition} />
        </Field>

        <label className="flex items-center gap-2.5 text-[13px]">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[rgb(var(--brand))]"
            checked={canPlayGoalkeeper}
            disabled={isGoalkeeper}
            onChange={(event) => setCanPlayGoalkeeper(event.target.checked)}
          />
          Aceita ir ao gol se faltar goleiro
        </label>

        <Field label="Nível estimado" hint="Ajuda o sorteio a equilibrar os times">
          <div className="pt-1">
            <StarRating value={skill} size={26} interactive onChange={setSkill} />
          </div>
        </Field>

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" full loading={add.isPending} disabled={name.trim().length < 2}>
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
